import { useEffect, useState } from "react";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import {
  AddNotesTableEntiresParamsType,
  DatabaseCommonTableProps,
} from "../../../Types/DatabaseTabTypes";
import {
  getColumns,
  notesDeletedTableMap,
  notesIdMap,
  NotesTableConstants,
  NotesValidationSchema,
} from "./constants";
import { message, Modal } from "antd";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import styles from "./NotesTable.module.scss";
import NotesTableForm from "./NotesTableForm";
import Form from "../../Form";
import { DatabaseSections, ModalModes } from "../../../enums/DatabaseEnums";
import { deleteFormSchema } from "../constants";
import DeleteConfirmation from "../DeleteConfirmation";

const NotesTable = (props?: DatabaseCommonTableProps) => {
  const { section, sectionId, setNotesCount, blockedJob } = props;
  const {
    FetchNotesTable,
    AddDatabaseTableEntries,
    DeleteDatabaseTableEntries,
    EditDatabaseTableEntries,
  } = CustomerDatabaseService();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [notesVal, setNotesVal] = useState<AddNotesTableEntiresParamsType>({});
  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  const { mutateAsync: addNotes, isPending: isAddingNotesPending } =
    AddDatabaseTableEntries();
  const { mutateAsync: deleteNotes, isPending: isDeletingNotesPending } =
    DeleteDatabaseTableEntries();
  const { mutateAsync: editNotes, isPending: isEditingNotesPending } =
    EditDatabaseTableEntries();
  const { data, refetch, isFetching } = FetchNotesTable({
    section: section,
    section_id: sectionId,
    page: currentPage,
    perPage: pageSize,
  });
  const {
    BUTTON: { ADD_NOTES_LABEL },
    MODAL: {
      ADD_NOTES_MODAL_TITLE,
      SUBMIT_BUTTON_LABEL,
      WIDTH,
      EDIT_NOTES_MODAL_TITLE,
    },
    MESSAGES: {
      ADD_NOTES_ERROR,
      ADD_NOTES_SUCCESSFUL,
      DELETE_NOTE_ERROR,
      DELETE_NOTE_SUCCESSFUL,
      EDIT_NOTE_ERROR,
      EDIT_NOTE_SUCCESSFUL,
    },
  } = NotesTableConstants;
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isDeleteNotesModalOpen, setIsDeleteNotesModalOpen] =
    useState<boolean>(false);
  const [notesId, setNotesId] = useState<string>("");
  const [modalMode, setModalMode] = useState<ModalModes>(ModalModes.CREATE);
  const handleNotesModalOpen = () => setIsNotesModalOpen(true);
  const handleNotesModalClose = () => setIsNotesModalOpen(false);
  const handleDeleteModalOpen = (id?: string) => {
    setNotesId(id);
    setIsDeleteNotesModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setNotesId("");
    setIsDeleteNotesModalOpen(false);
    setModalMode(ModalModes.CREATE);
  };
  const handleDelete = async (formVal?: object) => {
    await deleteNotes(
      {
        id: notesId,
        deletedTable: notesDeletedTableMap[section],
        notes: formVal?.archiveReason,
        ...(notesIdMap[section] ? { [notesIdMap[section]]: sectionId } : {}),
      },
      {
        onSuccess: () => {
          message.success(DELETE_NOTE_SUCCESSFUL);
          handleDeleteModalClose();
          refetch();
        },
        onError: () => {
          message.error(DELETE_NOTE_ERROR);
        },
      },
    );
  };
  const handleAddNotesSubmit = async (
    params?: AddNotesTableEntiresParamsType,
  ) => {
    if (modalMode === ModalModes.CREATE) {
      await addNotes(
        {
          ...params,
          id: DatabaseSections.NOTES,
          section: section,
          section_id: sectionId,
        },
        {
          onSuccess: () => {
            message.success(ADD_NOTES_SUCCESSFUL);
            refetch();
            handleNotesModalClose();
            setNotesVal({ typeofNotes: "", notes: "" });
          },
          onError: (err) => {
            message.error(err?.response?.data?.message ?? ADD_NOTES_ERROR);
          },
        },
      );
    }
    if (modalMode === ModalModes.EDIT) {
      await editNotes(
        {
          ...params,
          id: DatabaseSections.NOTES,
          section: section,
          section_id: sectionId,
          entityId: notesId,
        },
        {
          onSuccess: () => {
            message.success(EDIT_NOTE_SUCCESSFUL);
            refetch();
            handleNotesModalClose();
            setNotesVal({ typeofNotes: "", notes: "" });
            setNotesId("");
            setModalMode(ModalModes.CREATE);
          },
          onError: () => {
            message.error(EDIT_NOTE_ERROR);
          },
        },
      );
    }
  };
  const handleEdit = (id?: string) => {
    setNotesId(id);
    setModalMode(ModalModes.EDIT);
    const notesData = data?.data?.find((val) => val?.id === id);
    if (notesData) {
      setNotesVal({
        typeofNotes: notesData?.typeofNotes,
        notes: notesData?.notes,
      });
    }
    handleNotesModalOpen();
  };
  const columns = getColumns({
    handleDeleteModalOpen: handleDeleteModalOpen,
    handleEditNavigate: handleEdit,
    blockedJob: blockedJob,
  });

  useEffect(() => {
    refetch();
  }, [pageSize, currentPage]);

  useEffect(() => {
    setNotesCount(data?.meta?.totalRecords ?? 0);
  }, [data]);

  return (
    <>
      <Form validationSchema={deleteFormSchema}>
        {({ control: { handleSubmit } }) => (
          <DeleteConfirmation
            tabType={DatabaseSections.NOTES}
            deletePending={isDeletingNotesPending}
            isOpen={isDeleteNotesModalOpen}
            handleClose={handleDeleteModalClose}
            submitActionCall={handleSubmit(handleDelete)}
          />
        )}
      </Form>
      <Form validationSchema={NotesValidationSchema} values={notesVal}>
        {({ clearErrors, reset, control: { _formValues, handleSubmit } }) => {
          const handleCancel = () => {
            handleNotesModalClose();
            setNotesVal({
              typeofNotes: "",
              notes: "",
            });
            setNotesId("");
            clearErrors();
            reset();
            setModalMode(ModalModes.CREATE);
          };
          return (
            <Modal
              okText={SUBMIT_BUTTON_LABEL}
              onOk={handleSubmit(async () => {
                await handleAddNotesSubmit(_formValues);
                reset();
              })}
              okButtonProps={{
                loading:
                  modalMode === ModalModes.CREATE
                    ? isAddingNotesPending
                    : isEditingNotesPending,
              }}
              visible={isNotesModalOpen}
              onCancel={handleCancel}
              title={
                modalMode === ModalModes.CREATE
                  ? ADD_NOTES_MODAL_TITLE
                  : EDIT_NOTES_MODAL_TITLE
              }
              width={WIDTH}
              maskClosable={false}
              destroyOnClose={true}
            >
              <NotesTableForm />
            </Modal>
          );
        }}
      </Form>
      <CustomTableWithPagination
        columns={columns}
        data={data?.data}
        pageSize={pageSize}
        currentPage={currentPage}
        rowKey={(record) => record?.id}
        totalData={data?.meta?.totalRecords}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        loading={isFetching}
        className={styles.Table}
      />
      <TNButton
        text={ADD_NOTES_LABEL}
        type={ButtonType.PRIMARY}
        size={ButtonSize.LARGE}
        shape={ButtonShape.DEFAULT}
        onClick={handleNotesModalOpen}
        className={styles.addButton}
        disabled={!!blockedJob}
      />
    </>
  );
};

export default NotesTable;
